// packages/shared/src/services/apiClient.ts
//
// One-shot axios configuration for the cookie-based auth path.
// Each app calls `setupAxiosClient()` once from main.tsx; the global
// axios instance is then ready to:
//
//   1. Send the HttpOnly session cookie on every request
//      (axios.defaults.withCredentials = true). This is the actual auth
//      vehicle now; Authorization headers are no longer the source of
//      truth.
//
//   2. Copy the CSRF cookie value (`showliner_csrf`, plus an environment
//      suffix off production) into the `X-CSRF-Token`
//      header on every mutating request (POST/PUT/PATCH/DELETE). This
//      is the double-submit pattern — the backend's csrfMiddleware
//      compares header to cookie and rejects mismatches once
//      AUTH_COOKIE_ENFORCE_CSRF=true (PR2 flips that flag).
//
// We deliberately set this on the global `axios` instance rather than
// requiring every callsite to import a custom client. There are ~80+
// places in the codebase calling `axios.get/post/...` directly, and
// rewriting all of them would be its own multi-PR effort. The global
// approach gets cookies + CSRF for everyone for free; gradual
// migration to a named client can happen later.
//
// See docs/plans/AUTH_COOKIE_MIGRATION.md.

import axios from 'axios';
import { csrfCookieName } from '../utils/cookieNames';
import { getApiBaseUrl } from './apiConfig';

/**
 * The CSRF cookie name for the API this app talks to.
 *
 * Resolved LAZILY, per read, rather than captured at module load: the desktop
 * renderer calls `setApiBaseUrl()` at startup, which can land after this module
 * is evaluated. A captured name would silently be the production one, and the
 * failure — every mutating request rejected for a CSRF mismatch, reads only
 * failing on dev — is exactly the class this whole rename exists to end.
 *
 * Off production the name carries an environment suffix, because prod cookie
 * domain `.showliner.io` covers `*.dev.showliner.io` too, and two same-named
 * cookies mean `readCookie` below returns whichever the browser happens to put
 * first. See packages/shared/src/utils/cookieNames.ts.
 */
function currentCsrfCookieName(): string {
    return csrfCookieName(getApiBaseUrl());
}
const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/**
 * Read a cookie value from document.cookie. Returns null if missing.
 * The CSRF cookie is intentionally non-HttpOnly so JS can read it
 * here. The session cookie is HttpOnly and unreadable from JS — that
 * is the whole point of moving to cookies.
 */
function readCookie(name: string): string | null {
    try {
        const prefix = `${name}=`;
        const parts = document.cookie.split(';');
        for (const part of parts) {
            const trimmed = part.trim();
            if (trimmed.startsWith(prefix)) {
                return decodeURIComponent(trimmed.slice(prefix.length));
            }
        }
    } catch {
        /* document.cookie can throw in restricted contexts; treat as missing */
    }
    return null;
}

/**
 * Read the current CSRF token from the `showliner_csrf` cookie. Public
 * helper for the rare callsite that bypasses axios (raw `fetch`,
 * `WebSocket`, etc.) and needs to set the `X-CSRF-Token` header by
 * hand. Returns empty string when the cookie isn't present so callers
 * can spread it into a header bag without conditional logic.
 */
export function getCsrfToken(): string {
    return readCookie(currentCsrfCookieName()) ?? '';
}

let installed = false;

/**
 * Configure global axios for cookie-based auth + CSRF. Idempotent —
 * calling twice is harmless. Each app should call this once from
 * its main.tsx before rendering.
 */
export function setupAxiosClient(): void {
    if (installed) return;
    installed = true;

    // Cookies fly on every request. Browsers only attach cookies on
    // cross-origin XHR when withCredentials is set, AND the response
    // sets `Access-Control-Allow-Credentials: true` (corsMiddleware
    // already does this in PR1).
    axios.defaults.withCredentials = true;

    axios.interceptors.request.use((config) => {
        const method = (config.method || 'get').toLowerCase();
        if (!MUTATING_METHODS.has(method)) return config;

        const csrf = readCookie(currentCsrfCookieName());
        if (!csrf) return config;

        // Don't clobber if a callsite has set its own header (rare but
        // defensible — e.g. a test injecting a known value).
        const headers = config.headers ?? {};
        if (
            (headers as Record<string, unknown>)['X-CSRF-Token'] === undefined &&
            (headers as Record<string, unknown>)['x-csrf-token'] === undefined
        ) {
            (headers as Record<string, unknown>)['X-CSRF-Token'] = csrf;
        }
        config.headers = headers as typeof config.headers;
        return config;
    });
}
